/**
 * MetricUpsellModal (metric-tiering slice) — the measured-metric gate.
 * =====================================================================
 * Demo users browse the full catalogue with base performance open on every
 * cell (energy, power, capacity, voltage, current). The premium measured
 * metrics — impedance, thermal behaviour, batch & aging consistency, DC25
 * performance — are the PRO lever. Clicking a locked/teaser metric token opens
 * this upsell instead of revealing the number, category-framed to what the user
 * just reached for.
 *
 * Rendered once (in AppShell), driven by demoLimitStore.metricUpsellOpen. The
 * copy carries NO price — a single primary CTA "Talk to our team" routes to
 * /checkout with a per-category lead_source, and a tertiary "Keep exploring —
 * it's free" closes it (never a dead-end). First token click of the session
 * gets this modal; later ones get a slim toast (see openMetricUpsell).
 *
 * Structure/classes are shared with DailyLimitModal / ExportUpsellModal
 * (daily-limit-* CSS) so the modal is already styled, focus-trapped, and
 * reduced-motion-safe.
 */
import { createPortal } from 'preact/compat'
import { useEffect, useRef } from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import { trackEvent } from '../../modules/dataService.js'
import {
  metricUpsellOpen,
  metricUpsellCategory,
  closeMetricUpsell,
} from '../../state/demoLimitStore.js'

// Category-aware value line — falls back to a generic line for unknown keys.
const CATEGORY_COPY = {
  electrical: {
    eyebrow: 'Measured, not modelled',
    line: 'Impedance, DC25 performance and efficiency are measured on real hardware for every cell — unlock the exact numbers with PRO.',
  },
  thermal: {
    eyebrow: 'Measured, not modelled',
    line: 'Thermal behaviour — temperature rise and heat loss under load — is measured for every cell. Unlock the exact figures with PRO.',
  },
  quality: {
    eyebrow: 'Measured, not modelled',
    line: 'Cell-to-cell variation and post-formation aging are measured across a batch for every cell. Unlock the exact spread and loss with PRO.',
  },
}

export default function MetricUpsellModal() {
  const open = metricUpsellOpen.value
  const category = metricUpsellCategory.value
  const [, setLocation] = useLocation()
  const dialogRef = useRef(null)
  const primaryRef = useRef(null)
  const previouslyFocused = useRef(null)

  // Track the block once per open, focus the primary CTA, restore focus on close.
  useEffect(() => {
    if (!open) return
    previouslyFocused.current = document.activeElement
    trackEvent('Demo Metric Upsell Shown', { category: category || 'unknown' })
    const id = requestAnimationFrame(() => {
      if (primaryRef.current) primaryRef.current.focus()
    })
    return () => {
      cancelAnimationFrame(id)
      const prev = previouslyFocused.current
      if (prev && typeof prev.focus === 'function') prev.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!open) return null

  function close() {
    closeMetricUpsell()
  }

  function onOverlayClick(e) {
    if (e.target === e.currentTarget) close()
  }

  function onKeyDown(e) {
    if (e.key === 'Escape') {
      e.stopPropagation()
      close()
      return
    }
    // Minimal focus-trap: keep Tab within the dialog.
    if (e.key === 'Tab') {
      const focusables = dialogRef.current?.querySelectorAll(
        'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
      )
      if (!focusables || focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
  }

  function onPrimaryCta() {
    const categoryKey = category || 'unknown'
    trackEvent('Demo Metric Upgrade Clicked', {
      lead_source: `demo_metric_${categoryKey}`,
      category: categoryKey,
    })
    // Carry the attribution to the /contact inquiry submit (same mechanism as
    // DailyLimitModal / ExportUpsellModal), so the backend tags the sales
    // inquiry to the metric category the user reached for.
    try {
      sessionStorage.setItem('demo_lead_source', `demo_metric_${categoryKey}`)
    } catch (e) {
      /* sessionStorage unavailable (private mode) — attribution best-effort */
    }
    close()
    // Lightweight "Talk to our team" contact page (demo users have no price).
    setLocation('/contact')
  }

  const copy = CATEGORY_COPY[category] || {
    eyebrow: 'Measured, not modelled',
    line: 'Impedance, thermal, batch & aging are measured for every cell — unlock the exact numbers with PRO.',
  }

  return createPortal(
    <div className="daily-limit-overlay" onClick={onOverlayClick} onKeyDown={onKeyDown}>
      <div
        className="daily-limit-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="metric-upsell-title"
        ref={dialogRef}
      >
        <button
          className="daily-limit-modal__close"
          onClick={close}
          aria-label="Close"
          type="button"
        >
          ×
        </button>

        <div className="daily-limit-modal__body">
          <p className="daily-limit-modal__eyebrow">{copy.eyebrow}</p>
          <h2 className="daily-limit-modal__title" id="metric-upsell-title">
            This is a PRO metric.
          </h2>

          <p className="daily-limit-modal__reframe">
            {copy.line}{' '}
            <strong>Base performance stays open for every cell.</strong>
          </p>

          {/* One value-framed primary CTA (no price shown) */}
          <button
            className="daily-limit-modal__cta"
            onClick={onPrimaryCta}
            ref={primaryRef}
            type="button"
          >
            Talk to our team
          </button>
          <p className="daily-limit-modal__risk-off">
            No commitment — just a conversation. Most teams hear back within one
            business day.
          </p>

          {/* Social proof (shared styling with the daily-limit modal) */}
          <div className="daily-limit-modal__proof">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2l2.4 7.4H22l-6 4.4 2.3 7.2L12 16.6 5.7 21l2.3-7.2-6-4.4h7.6z" />
            </svg>
            Trusted by OEMs and tier-1 suppliers for measured cell data.
          </div>

          {/* Tertiary — no dead-end */}
          <div className="daily-limit-modal__secondary">
            <button className="daily-limit-modal__tertiary" onClick={close} type="button">
              Keep exploring — it's free
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
